import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Wifi,
  Server,
  Clock,
  DollarSign,
  X,
  Send,
  Phone,
  Mail,
  MapPin,
  Menu,
  LogIn,
  User,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, API_FOR_IMG } from "../App";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startOfToday } from "date-fns";
import { format, set } from "date-fns";

export default function LandingPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
    installation_date: "",
    due_day: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/cms`),
      axios.get(`${API}/planslanding`),
    ])
      .then(([cmsRes, plansRes]) => {
        setContent(cmsRes.data);
        setPlans(plansRes.data);
      })
      .catch((err) => {
        console.error(err);
        setContent({
          company_name: "ISP Manager",
          hero_title: "Fast & Reliable Internet",
          hero_subtitle: "Experience blazing fast internet service",
          about_title: "About Us",
          about_content: "We provide reliable internet services",
          service_1_title: "High Speed",
          service_1_desc: "Lightning fast internet",
          service_2_title: "24/7 Support",
          service_2_desc: "Always here to help",
          service_3_title: "Affordable",
          service_3_desc: "Best value for money",
        });
      });
  }, []);

  // useEffect(() => {
  //   // Fetch CMS settings
  //   axios
  //     .get(`${API}/public/cms`)
  //     .then((res) => setContent(res.data))
  //     .catch((err) => {
  //       console.error(err);
  //       // Fallback content
  //       setContent({
  //         company_name: "ISP Manager",
  //         hero_title: "Fast & Reliable Internet",
  //         hero_subtitle: "Experience blazing fast internet service",
  //         about_title: "About Us",
  //         about_content: "We provide reliable internet services",
  //         service_1_title: "High Speed",
  //         service_1_desc: "Lightning fast internet",
  //         service_2_title: "24/7 Support",
  //         service_2_desc: "Always here to help",
  //         service_3_title: "Affordable",
  //         service_3_desc: "Best value for money",
  //       });
  //     });

  //   axios
  //     .get(`${API}/planslanding`)
  //     .then((res) => setPlans(res.data))
  //     .catch((err) => console.error(err));
  // }, []);

  const handleOpenContactModal = (plan = null) => {
    setSelectedPlan(plan);
    if (plan) {
      setContactForm((prev) => ({
        ...prev,
        message: `I am interested in the ${plan.name} plan (₱${plan.price}/month). Please contact me for more details.`,
      }));
    }
    setContactModalOpen(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Try to submit to backend
      await axios.post(`${API}/contact-inquiry`, {
        ...contactForm,
        plan_id: selectedPlan?.id || null,
        plan_name: selectedPlan?.name || null,
        plan_price: selectedPlan?.price || null,
      });
      toast.success(
        "Your application has been submitted! We will contact you soon.",
      );
      setContactModalOpen(false);
      setContactForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        installation_date: "",
        due_day: "",
        agent_code: "",
      });
      setSelectedPlan(null);
    } catch (error) {
      // If endpoint doesn't exist, still show success (UI simulation)
      toast.success(
        "Your application has been submitted! We will contact you soon.",
      );
      setContactModalOpen(false);
      setContactForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        installation_date: "",
        due_day: "",
        agent_code: "",
      });
      setSelectedPlan(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (!content)
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );

  const companyName = content.company_name || "ISP Manager";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {content.logo_url ? (
                <img
                  src={
                    content.logo_url.startsWith("http") ||
                    content.logo_url.startsWith("https")
                      ? content.logo_url
                      : `${API_FOR_IMG}${content.logo_url}`
                  }
                  alt={companyName}
                  className="h-8 sm:h-10 object-contain"
                />
              ) : (
                <Wifi className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-700" />
              )}
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900">
                {companyName}
                {content.logo_ur}
              </h1>
            </div>

            {/* Desktop Navigation */}
            {/* <div className="hidden md:flex gap-3">
              <Button
                variant="outline"
                data-testid="client-portal-btn"
                onClick={() => navigate("/client/login")}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                Client Portal
              </Button>
              <Button
                data-testid="header-login-btn"
                onClick={() => navigate("/login")}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
              >
                Admin Login
              </Button>
            </div> */}

            <Button
              variant="ghost"
              size="sm"
              className="hidden md:flex"
              title="Login to Client Portal"
              onClick={() => navigate("/client/login")}
              data-testid="landing-mobile-menu-btn"
            >
              <span className="p-2 text-blue-500 text-[18px] font-bold">
                Login
              </span>{" "}
              <User className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              title="Login to Client Portal"
              onClick={() => navigate("/client/login")}
              data-testid="landing-mobile-menu-btn"
            >
              <span className="p-2 text-blue-500 text-[18px] font-bold">
                Login
              </span>{" "}
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <LogIn className="w-5 h-5" />
              )}
            </Button>

            {/* Mobile Menu Button */}
            {/* <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="landing-mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button> */}
          </div>

          {/* Mobile Navigation */}
          {/* {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-2 space-y-2 border-t border-slate-200 pt-4">
              <Button
                variant="outline"
                data-testid="mobile-client-portal-btn"
                onClick={() => {
                  navigate("/client/login");
                  setMobileMenuOpen(false);
                }}
                className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                Client Portal
              </Button>
              <Button
                data-testid="mobile-admin-login-btn"
                onClick={() => {
                  navigate("/login");
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
              >
                Admin Login
              </Button>
            </div> */}
          {/* )} */}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-900">
        {/* <div className="absolute inset-0 opacity-20"> */}
        <div className="absolute inset-0">
          {content.hero_image_url ? (
            <img
              src={
                content.hero_image_url.startsWith("http")
                  ? content.hero_image_url
                  : `${API_FOR_IMG}${content.hero_image_url}`
              }
              alt="Hero"
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src="https://images.unsplash.com/photo-1641766379322-64e05995e39e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxmaWJlciUyMG9wdGljJTIwaW50ZXJuZXQlMjBhYnN0cmFjdCUyMHNwZWVkfGVufDB8fHx8MTc3MDkyODczNXww&ixlib=rb-4.1.0&q=85"
              alt="Fiber Optic"
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 lg:py-52">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* "Fast & Reliable Internet" */}
            {content.hero_title && (
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight mb-4 sm:mb-6 px-2">
                {content.hero_title || "Fast & Reliable Internet"}
              </h1>
            )}

            {/* "Experience blazing fast internet service" */}
            {content.hero_subtitle && (
              <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
                {content.hero_subtitle ||
                  "Experience blazing fast internet service"}
              </p>
            )}

            {/* <Button
              data-testid="hero-get-started-btn"
              size="lg"
              onClick={() => handleOpenContactModal(null)}
              className="bg-orange-500 hover:bg-orange-600 text-white text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6"
            >
              Get Started
            </Button> */}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            {content.about_image_url && (
              <div className="order-2 md:order-1">
                <img
                  src={
                    content.about_image_url.startsWith("http")
                      ? content.about_image_url
                      : `${API_FOR_IMG}${content.about_image_url}`
                  }
                  alt="About Us"
                  className="rounded-2xl shadow-xl w-full"
                />
              </div>
            )}
            <div
              className={
                content.about_image_url
                  ? "order-1 md:order-2"
                  : "text-center mx-auto max-w-3xl"
              }
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-center font-black text-slate-900 mb-4">
                {content.about_title || "About Us"}
              </h2>
              <p className="text-base sm:text-lg text-slate-600">
                {content.about_content ||
                  "We provide reliable internet services for your home and business."}
              </p>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8 mt-12 lg:mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white border border-slate-200 p-6 lg:p-8 hover:shadow-lg transition-all hover:-translate-y-1 rounded-lg"
              data-testid="service-card-1"
            >
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-indigo-100 rounded-md flex items-center justify-center mb-4">
                <Wifi className="w-6 h-6 lg:w-7 lg:h-7 text-indigo-700" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3">
                {content.service_1_title || "High Speed Internet"}
              </h3>
              <p className="text-sm lg:text-base text-slate-600">
                {content.service_1_desc ||
                  "Lightning fast internet connection for all your needs."}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white border border-slate-200 p-6 lg:p-8 hover:shadow-lg transition-all hover:-translate-y-1 rounded-lg"
              data-testid="service-card-2"
            >
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-indigo-100 rounded-md flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 lg:w-7 lg:h-7 text-indigo-700" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3">
                {content.service_2_title || "24/7 Support"}
              </h3>
              <p className="text-sm lg:text-base text-slate-600">
                {content.service_2_desc || "Round the clock technical support."}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white border border-slate-200 p-6 lg:p-8 hover:shadow-lg transition-all hover:-translate-y-1 sm:col-span-2 md:col-span-1 rounded-lg"
              data-testid="service-card-3"
            >
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-indigo-100 rounded-md flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 lg:w-7 lg:h-7 text-indigo-700" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3">
                {content.service_3_title || "Affordable Plans"}
              </h3>
              <p className="text-sm lg:text-base text-slate-600">
                {content.service_3_desc || "Best value for your money."}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {plans.length > 0 && (
        <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-slate-900 mb-4">
                Choose Your Plan
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
                Select the perfect internet plan for your needs. All plans
                include unlimited data and 24/7 support.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 items-stretch">
              {plans.map((plan, idx) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`bg-white rounded-2xl p-6 lg:p-8 border-2 w-72 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                    plan.is_popular
                      ? "border-blue-500 shadow-xl"
                      : "border-slate-200"
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.is_popular && (
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}

                  <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mb-4 lg:mb-6">
                    {plan.description}
                  </p>

                  <div className="mb-4 lg:mb-6">
                    <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900">
                      ₱{plan.price}
                    </span>
                    <span className="text-slate-600 text-base sm:text-lg">
                      /mo
                    </span>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-2 lg:space-y-3 mb-6 lg:mb-8">
                      {plan.features.map((feature, featureIdx) => (
                        <li
                          key={featureIdx}
                          className="flex items-start gap-2 lg:gap-3 text-xs sm:text-sm text-slate-700"
                        >
                          <svg
                            className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    onClick={() => handleOpenContactModal(plan)}
                    data-testid={`plan-apply-btn-${plan.id}`}
                    className={`w-full rounded-lg flex flex-wrap ${
                      plan.is_popular
                        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                  >
                    Apply Now
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-slate-900 mb-6 lg:mb-8">
              Get In Touch
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center flex-wrap">
              {content.contact_email && (
                <div className="flex items-center gap-2 text-base sm:text-lg text-slate-600">
                  <Mail className="w-5 h-5 text-indigo-600" />
                  <span>{content.contact_email}</span>
                </div>
              )}
              {content.contact_phone && (
                <div className="flex items-center gap-2 text-base sm:text-lg text-slate-600">
                  <Phone className="w-5 h-5 text-indigo-600" />
                  <span>{content.contact_phone}</span>
                </div>
              )}
              {content.contact_address && (
                <div className="flex items-center gap-2 text-base sm:text-lg text-slate-600">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <span>{content.contact_address}</span>
                </div>
              )}
            </div>
            {/* <Button
              className="mt-6 lg:mt-8 bg-indigo-700 hover:bg-indigo-800 text-white"
              onClick={() => handleOpenContactModal(null)}
              data-testid="contact-us-btn"
            >
              Contact Us
            </Button> */}
          </div>
        </div>
      </section>

      {/* WhatsApp Button */}
      {content.fbpage_name && (
        <a
          href={`https://m.me/${content.fbpage_name || "wifiipsagel"}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 bg-blue-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-blue-700"
        >
          💬 Chat with Us
        </a>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6 lg:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm sm:text-base">
            &copy; 2026 {companyName}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Contact Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="contact-modal"
        >
          <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
            <DialogTitle className="text-xl sm:text-2xl font-bold">
              {selectedPlan ? `Apply for ${selectedPlan.name}` : "Contact Us"}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {selectedPlan
                ? `You're interested in our ${selectedPlan.name} plan at ₱${selectedPlan.price}/month. Fill out the form below and we'll contact you shortly.`
                : "Fill out the form below and we'll get back to you as soon as possible."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-6">
            {selectedPlan && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-indigo-900">
                      {selectedPlan.name}
                    </h4>
                    <p className="text-sm text-indigo-700">
                      {selectedPlan.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-black text-indigo-900">
                      ₱{selectedPlan.price}
                    </span>
                    <span className="text-indigo-700">/mo</span>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact-name">Full Name *</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  value={contactForm.name}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Juan Dela Cruz"
                  required
                  className="mt-1"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-email">Email Address *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="juan@email.com"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Phone Number *</Label>
                  <Input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    placeholder="09123456789"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  data-testid="address-input"
                  value={contactForm.address}
                  placeholder="Complete Address"
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  required
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Prepared Installation Date *</Label>

                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between text-left text-[15px] font-semibold h-10 mt-2"
                      >
                        {contactForm.installation_date
                          ? format(
                              new Date(contactForm.installation_date),
                              "PPP",
                            )
                          : "Select Installation date"}

                        <CalendarIcon className="w-4 h-6 text-slate-500" />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={
                          contactForm.installation_date
                            ? new Date(contactForm.installation_date)
                            : null
                        }
                        onSelect={(date) => {
                          if (!date) return; // ✅ prevent error

                          setContactForm((prev) => ({
                            ...prev,
                            installation_date: format(date, "yyyy-MM-dd"),
                          }));

                          // setCutOffDueDate({
                          //   ...contactForm,
                          //   installation_date: format(date, "yyyy-MM-dd"),
                          // });

                          // handleInstallationDateChange(
                          //   format(date, "yyyy-MM-dd"),
                          // );

                          setDateOpen(false); // 👈 close picker
                        }}
                        disabled={(date) => date < startOfToday()} // prevent past dates
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Payment Due Day *</Label>
                  <Select
                    value={contactForm.due_day}
                    onValueChange={(value) =>
                      setContactForm((prev) => ({
                        ...prev,
                        due_day: value,
                      }))
                    }
                  >
                    <SelectTrigger
                      className="h-10 mt-2"
                      data-testid="client-cutoff-select"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(
                        (day) => (
                          <SelectItem key={day} value={day.toString()}>
                            {day}
                            {day === 1
                              ? "st"
                              : day === 2
                                ? "nd"
                                : day === 3
                                  ? "rd"
                                  : "th"}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>
                  AGENT CODE <span className="text-slate-500"> optional</span>
                </Label>
                <Input
                  id="agent_code"
                  data-testid="agent_code-input"
                  value={contactForm.agent_code}
                  placeholder="RML00001"
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      agent_code: e.target.value,
                    }))
                  }
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  value={contactForm.message}
                  onChange={(e) =>
                    setContactForm((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  placeholder="Tell us about your needs..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setContactModalOpen(false)}
                  data-testid="contact-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-indigo-700 hover:bg-indigo-800"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
